import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { GlassCard } from '../components/GlassCard';
import { Clock, ChevronUp, ChevronDown } from 'lucide-react';
function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso; }
}
function todayISO() { return new Date().toISOString().slice(0, 10); }

const BUCKETS = [
  { key: 'current', label: 'Current', color: '#14B8A6', bg: 'bg-teal-500/10 border-teal-500/20' },
  { key: 'bucket_31_60', label: '31–60 days', color: '#F59E0B', bg: 'bg-amber-500/10 border-amber-500/20' },
  { key: 'bucket_61_90', label: '61–90 days', color: '#F97316', bg: 'bg-orange-500/10 border-orange-500/20' },
  { key: 'bucket_over_90', label: '>90 days', color: '#EF4444', bg: 'bg-red-500/10 border-red-500/20' },
];

const BUCKET_CHIP = {
  current: 'bg-teal-500/15 text-teal-400 border-teal-500/25',
  bucket_31_60: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
  bucket_61_90: 'bg-orange-500/15 text-orange-400 border-orange-500/25',
  bucket_over_90: 'bg-red-500/15 text-red-400 border-red-500/25',
};

const BUCKET_LABEL = {
  current: 'Current',
  bucket_31_60: '31–60d',
  bucket_61_90: '61–90d',
  bucket_over_90: '>90d',
};

export default function ErpAging() {
  const { token } = useAuth();
  const toast = useToast();
  const { formatMoney } = useTenantFormat();

  const [type, setType] = useState('receivable');
  const [asOf, setAsOf] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('days_outstanding');
  const [sortDir, setSortDir] = useState('desc');

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const r = await api.erpAging(token, { type, as_of: asOf });
      setData(r);
    } catch (e) {
      if (e.status === 403) return;
      toast.show(e.message || 'Failed to load aging', 'error');
    } finally { setLoading(false); }
  }, [token, type, asOf, toast]);

  useEffect(() => { load(); }, [load]);

  const sorted = useMemo(() => {
    if (!data?.aging) return [];
    return [...data.aging].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const totals = data?.totals || {};
  const bucketCounts = {};
  (data?.aging || []).forEach(r => {
    bucketCounts[r.bucket] = (bucketCounts[r.bucket] || 0) + 1;
  });

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="erp-aging-page">
      {/* Top controls */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Type toggle */}
        <div className="flex p-1 rounded-xl bg-white/5 border border-white/10" data-testid="erp-aging-toggle">
          {['receivable', 'payable'].map(t => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`px-4 py-2 rounded-lg text-xs font-bold capitalize transition-all ${type === t ? 'bg-[#2563EB]/20 text-[#2563EB] border border-[#2563EB]/25' : 'text-white/50 hover:text-white/80'}`}
              data-testid={`erp-aging-type-${t}`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* As-of date */}
        <input
          type="date"
          value={asOf}
          onChange={e => setAsOf(e.target.value)}
          data-testid="erp-aging-as-of"
          className="h-10 px-3 rounded-xl bg-white/[0.05] border border-white/10 text-white text-sm focus:outline-none focus:border-[#2563EB]/40"
        />
      </div>

      {loading ? (
        <div className="py-12 flex justify-center">
          <div className="w-6 h-6 border-2 border-[#2563EB] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : data ? (
        <>
          {/* 4 Bucket KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {BUCKETS.map(b => (
              <div
                key={b.key}
                data-testid={`erp-aging-bucket-${b.key}`}
                className={`p-4 rounded-2xl border ${b.bg}`}
              >
                <p className="text-white/50 text-[10px] font-bold uppercase tracking-wider">{b.label}</p>
                <p className="font-extrabold text-lg tabular-nums mt-1" style={{ color: b.color }}>{formatMoney(totals[b.key])}</p>
                <p className="text-white/30 text-[10px] mt-0.5">{bucketCounts[b.key] || 0} invoices</p>
              </div>
            ))}
          </div>

          {/* Grand total */}
          <GlassCard dark className="p-5">
            <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider">Grand Total ({type})</p>
            <p className="text-white font-extrabold text-2xl tabular-nums mt-1">{formatMoney(totals.grand_total)}</p>
          </GlassCard>

          {/* Aging table */}
          <GlassCard dark className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-white/[0.04] border-b border-white/10">
                    {[
                      { key: 'party_name', label: 'Party' },
                      { key: 'invoice_id', label: 'Invoice #' },
                      { key: 'invoice_date', label: 'Invoice Date' },
                      { key: 'days_outstanding', label: 'Days Outstanding' },
                      { key: 'amount_rupees', label: 'Amount' },
                      { key: 'bucket', label: 'Bucket' },
                    ].map(col => (
                      <th
                        key={col.key}
                        onClick={() => toggleSort(col.key)}
                        className="px-5 py-3 text-left text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70 select-none whitespace-nowrap"
                      >
                        <span className="inline-flex items-center gap-1">
                          {col.label}
                          {sortKey === col.key && (sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td colSpan={6} className="py-12 text-center text-white/40 text-sm">No {type} aging data</td></tr>
                  ) : sorted.map((r, i) => (
                    <tr key={`${r.invoice_id}-${i}`} data-testid={`erp-aging-row-${r.invoice_id}`} className={`border-b border-white/[0.04] hover:bg-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.02]' : ''}`}>
                      <td className="px-5 py-3 text-white font-semibold text-xs">{r.party_name}</td>
                      <td className="px-5 py-3 text-white/60 text-xs tabular-nums">{r.invoice_id}</td>
                      <td className="px-5 py-3 text-white/60 text-xs tabular-nums">{fmtDate(r.invoice_date)}</td>
                      <td className="px-5 py-3 text-white/80 text-xs tabular-nums font-bold">{r.days_outstanding}d</td>
                      <td className="px-5 py-3 text-white text-xs tabular-nums font-bold text-right">{formatMoney(r.amount_rupees)}</td>
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${BUCKET_CHIP[r.bucket] || 'bg-white/5 text-white/40 border-white/10'}`}>
                          {BUCKET_LABEL[r.bucket] || r.bucket}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </>
      ) : null}
    </div>
  );
}


